/**
 * Shared geometry and layout helpers for all sauna engines.
 *
 * Every engine (square, barrel, two-room) needs to add positioned, shadow-casting
 * meshes to groups. These helpers were previously duplicated in each engine file.
 *
 * Most of the file is Three.js mesh construction; `spreadEvenPositions` is the
 * one pure-arithmetic exception, kept here because member spacing and member
 * placement are the same job and every caller needs both.
 *
 * CRITICAL OPTIMIZATION: We use a single shared BoxGeometry(1, 1, 1) for all
 * box meshes, applying dimensions via mesh.scale instead of creating unique
 * geometry per mesh. This reduces GPU memory from O(N) geometry buffers to O(1),
 * preventing WebGL context loss on scenes with hundreds of meshes.
 */
import * as THREE from "three";

// ─── Shared Geometry Singletons ─────────────────────────────

/**
 * Make a geometry outlive every scene that uses it, by construction.
 *
 * "NEVER dispose this" used to be a comment plus a `SHARED_GEOMETRIES` membership
 * check at each imperative teardown site — which covers the traversals in
 * `sceneDisposal.ts` and nothing else. It does NOT cover R3F: a `<mesh
 * geometry={UNIT_BOX}>` is disposed by the reconciler on unmount unless every such
 * mesh remembers `dispose={null}`, and one forgotten prop silently frees the buffer
 * every other mesh in the app is still pointing at.
 *
 * Neutering `dispose` moves the invariant into the object that owns it: one place
 * instead of one per consumer, enforced rather than documented, and checkable in a
 * plain unit test with no renderer. Freeing the buffer is not something any caller
 * should ever want — these live for the process.
 */
function shareForProcessLifetime<T extends THREE.BufferGeometry>(
	geometry: T,
): T {
	geometry.dispose = () => {};
	return geometry;
}

/** Single unit box reused by ALL addBox() calls. Disposal is a no-op by design. */
export const UNIT_BOX = shareForProcessLifetime(new THREE.BoxGeometry(1, 1, 1));

/**
 * Single unit cylinder (diameter 1, height 1, axis +Y) reused by ALL
 * addCylinder() calls — round hardware (vent plate valves, flanges) the same
 * way UNIT_BOX serves boxes. Disposal is a no-op by design; it is also registered
 * in `SHARED_GEOMETRIES` so the imperative teardown traversals skip it.
 */
export const UNIT_CYLINDER = shareForProcessLifetime(
	new THREE.CylinderGeometry(0.5, 0.5, 1, 24),
);

export { shareForProcessLifetime };

// ─── Core Mesh Helper ───────────────────────────────────────

/** Add any geometry as a shadow-casting mesh at (x, y, z) in a group. */
export function addMesh(
	geom: THREE.BufferGeometry,
	mat: THREE.Material,
	group: THREE.Group,
	x = 0,
	y = 0,
	z = 0,
): THREE.Mesh {
	const mesh = new THREE.Mesh(geom, mat);
	mesh.position.set(x, y, z);
	mesh.castShadow = true;
	mesh.receiveShadow = true;
	group.add(mesh);
	return mesh;
}

// ─── Layout Helpers ─────────────────────────────────────────

/**
 * Evenly spaced member centres over `[from, to]`, at most `maxSpacing` apart,
 * with a member ON each end so the run is supported at both ends.
 *
 * This is the one spacing convention for repeated framing members — roof
 * joists, ceiling battens. Two hand-rolled copies of `ceil(span / cc) + 1`
 * are how a batten layer ends up at a different pitch from the layer it is
 * nailed to (issue 115).
 */
export function spreadEvenPositions(
	from: number,
	to: number,
	maxSpacing: number,
): number[] {
	const span = Math.max(0, to - from);
	const count = Math.max(2, Math.ceil(span / maxSpacing) + 1);
	const step = span / (count - 1);
	return Array.from({ length: count }, (_, index) => from + index * step);
}

// ─── Box Helpers ────────────────────────────────────────────

/**
 * Add a box at (x, y, z) using the shared unit geometry.
 * Dimensions are applied via mesh.scale, so no new geometry is allocated.
 */
export function addBox(
	w: number,
	h: number,
	d: number,
	mat: THREE.Material,
	x: number,
	y: number,
	z: number,
	group: THREE.Group,
): THREE.Mesh {
	const mesh = new THREE.Mesh(UNIT_BOX, mat);
	mesh.scale.set(w, h, d);
	mesh.position.set(x, y, z);
	mesh.castShadow = true;
	mesh.receiveShadow = true;
	group.add(mesh);
	return mesh;
}

/**
 * Add a disc/cylinder (axis +Y) at (x, y, z) using the shared unit cylinder.
 * Dimensions are applied via mesh.scale, so no new geometry is allocated.
 * Rotate the PARENT group to point the axis along a wall normal.
 */
export function addCylinder(
	radius: number,
	height: number,
	mat: THREE.Material,
	x: number,
	y: number,
	z: number,
	group: THREE.Group,
): THREE.Mesh {
	const mesh = new THREE.Mesh(UNIT_CYLINDER, mat);
	mesh.scale.set(radius * 2, height, radius * 2);
	mesh.position.set(x, y, z);
	mesh.castShadow = true;
	mesh.receiveShadow = true;
	group.add(mesh);
	return mesh;
}

/**
 * Add a box with dedicated geometry sized to its final dimensions.
 *
 * Use this for highly visible textured surfaces where UVs need to respect
 * the real face size instead of inheriting the unit-cube mapping from
 * `addBox()`.
 */
export function addSizedBox(
	w: number,
	h: number,
	d: number,
	mat: THREE.Material,
	x: number,
	y: number,
	z: number,
	group: THREE.Group,
): THREE.Mesh {
	return addMesh(new THREE.BoxGeometry(w, h, d), mat, group, x, y, z);
}

/**
 * Box where only the TOP vertices are tilted along a pitch.
 * Used for wall panels that meet a sloped roof at their top edge.
 *
 * NOTE: These MUST create unique geometry because they modify vertex positions.
 * They are few in number so the impact is minimal.
 */
export function addSlopedTopBox(
	w: number,
	h: number,
	d: number,
	mat: THREE.Material,
	x: number,
	y: number,
	z: number,
	group: THREE.Group,
	pitchRad: number,
	axis: "x" | "z" = "z",
): THREE.Mesh {
	const geom = new THREE.BoxGeometry(w, h, d);
	const pos = geom.attributes.position;
	for (let i = 0; i < pos.count; i++) {
		const vy = pos.getY(i);
		const slope = axis === "x" ? pos.getX(i) : pos.getZ(i);
		if (vy > 0) {
			pos.setY(i, vy + slope * Math.tan(pitchRad));
		}
	}
	geom.computeVertexNormals();
	return addMesh(geom, mat, group, x, y, z);
}

/**
 * Box where ALL vertices are tilted along a pitch.
 * Used for top plates and fascia boards that follow the roof slope.
 *
 * NOTE: These MUST create unique geometry because they modify vertex positions.
 * They are few in number so the impact is minimal.
 */
export function addSlopedBox(
	w: number,
	h: number,
	d: number,
	mat: THREE.Material,
	x: number,
	y: number,
	z: number,
	group: THREE.Group,
	pitchRad: number,
	axis: "x" | "z" = "z",
): THREE.Mesh {
	const geom = new THREE.BoxGeometry(w, h, d);
	const pos = geom.attributes.position;
	for (let i = 0; i < pos.count; i++) {
		const vy = pos.getY(i);
		const slope = axis === "x" ? pos.getX(i) : pos.getZ(i);
		pos.setY(i, vy + slope * Math.tan(pitchRad));
	}
	geom.computeVertexNormals();
	return addMesh(geom, mat, group, x, y, z);
}
